import React, { useState } from 'react';
import { Copy, Check, Code, LinkIcon, FileText, BookOpen, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Message } from '../../../../types/interviews';

interface TextMessageContentProps {
  message: Message;
}

const TextMessageContent: React.FC<TextMessageContentProps> = ({ message }) => {
  const [copied, setCopied] = useState(false);
  
  if (!message.textMessageData) return null;
  
  const { message: content, message_type, language } = message.textMessageData;
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Get icon based on message type with proper color scheme
  const getIcon = () => {
    switch (message_type) {
      case 'code':
        return <Code size={14} className="text-primary-light dark:text-primary-dark" />;
      case 'url':
        return <LinkIcon size={14} className="text-secondary-light dark:text-secondary-dark" />;
      case 'problem':
        return <Code size={14} className="text-primary-light dark:text-primary-dark" />;
      case 'explanation':
        return <BookOpen size={14} className="text-text-secondary-light dark:text-text-secondary-dark" />;
      default:
        return <FileText size={14} className="text-text-secondary-light dark:text-text-secondary-dark" />;
    }
  };
  
  // Get type label
  const getTypeLabel = () => {
    switch (message_type) {
      case 'code':
        return language ? `Code (${language})` : 'Code';
      case 'url':
        return 'Link';
      case 'problem':
        return 'Problem';
      case 'explanation':
        return 'Explanation';
      default:
        return 'Message';
    }
  };
  
  return (
    <div className="w-full">
      {/* Header with icon and copy button */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-border-light/40 dark:border-border-dark/40">
        <div className="flex items-center gap-2">
          {getIcon()}
          <span className="text-xs font-medium text-text-secondary-light dark:text-text-secondary-dark">
            {getTypeLabel()}
          </span>
        </div>
        
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-background-light/60 dark:hover:bg-background-dark/60 transition-colors duration-200"
          title="Copy to clipboard"
        >
          {copied ? (
            <>
              <Check size={14} className="text-secondary-light dark:text-secondary-dark" />
              <span className="text-xs text-secondary-light dark:text-secondary-dark font-medium">Copied!</span>
            </>
          ) : (
            <>
              <Copy size={14} className="text-text-secondary-light dark:text-text-secondary-dark" />
              <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">Copy</span>
            </>
          )}
        </button>
      </div>
      
      {/* Content area */}
      <div className={`text-sm text-text-primary-light dark:text-text-primary-dark ${
        message_type === 'code' 
          ? 'font-mono bg-surface-light/50 dark:bg-surface-dark/50 p-3 rounded-lg overflow-x-auto border border-border-light/10 dark:border-border-dark/10' 
          : ''
      }`}>
        {message_type === 'url' ? (
          <a 
            href={content.startsWith('http') ? content : `https://${content}`}
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary-light dark:text-primary-dark hover:text-primary-hover-light dark:hover:text-primary-hover-dark underline break-all transition-colors duration-200"
          >
            {content}
          </a>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown 
              components={{
                p: ({node, ...props}) => <p className="mb-2 leading-relaxed" {...props} />,
                h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2 mt-3" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2 mt-2.5" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-1.5 mt-2" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2 space-y-1" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2 space-y-1" {...props} />,
                li: ({node, ...props}) => <li className="ml-0" {...props} />,
                code: ({className, ...props}: any) => 
                  className ? (
                    <code className="block bg-surface-light/50 dark:bg-surface-dark/50 p-3 rounded-lg font-mono text-xs overflow-x-auto mb-2" {...props} />
                  ) : (
                    <code className="bg-surface-light/50 dark:bg-surface-dark/50 px-1.5 py-0.5 rounded text-xs font-mono" {...props} />
                  ),
                pre: ({node, ...props}) => <pre className="bg-surface-light/50 dark:bg-surface-dark/50 p-3 rounded-lg overflow-x-auto mb-2 border border-border-light/10 dark:border-border-dark/10" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-primary-light/30 dark:border-primary-dark/30 pl-3 italic my-2" {...props} />,
                a: ({node, ...props}) => <a className="text-primary-light dark:text-primary-dark hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
      
      {/* Timestamp */}
      <p className="text-[11px] mt-2 text-text-tertiary-light dark:text-text-tertiary-dark text-right">
        {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
      </p>
    </div>
  );
};

export default TextMessageContent;
